import {
  cruiseOfferTileClick,
  offerClicked,
} from 'analytics/eventDefinitions'
import { addGTMEvent, fireInteractionEvent } from 'api/googleTagManager'
import CurrencyContext from 'contexts/currencyContext'
import useOffer from 'hooks/Offers/useOffer'
import { isCruiseOffer, isHotel, isLEOffer } from 'lib/offer/offerTypes'
import type { RefCallback } from 'react'
import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import FrontPageTile from './FrontPageOfferTiles/FrontPageTile'
import OfferSearchTile from './SearchOfferTiles/OfferSearchTile'
import type { OfferListEventHandler } from '../OfferListEventsContext'
import OfferListEventsContext, {
  OfferListEvents,
  OfferListEventsProvider,
} from '../OfferListEventsContext'
import OfferListLoadingOfferTile from './OfferListLoadingOfferTile'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import HeroOfferTile from './HeroOfferTiles/HeroOfferTile'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import {
  calculateHasBeenInView,
  calculateTrackingInView,
  handleSkipInView,
} from 'components/utils/OfferInView'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { calculateDaysBetweenDates } from 'lib/payment/payInInstalmentsUtils'

interface Props {
  /**
   * The visual style of tile that should be rendered
   */
  tileStyle: 'search' | 'frontpage' | 'hero'
  /**
   * The offer ID this tile is for
   */
  offerId: string
  /**
   * The filters associated with the list that's rendering this tile
   */
  filters?: App.OfferListFilters
  /**
   * The position of this tile in the offer list.
   * Used for analytics
   */
  position: number
  /**
   * Will eager load (not wait till in view) the first image of whatever image carousel is shown for this offer
   * Use this mostly for SSR
   */
  eagerLoadImage?: boolean
  className?: string
  /**
   * Dictate the behaviour of when the tile will mount/load
   *
   * offer-and-inview:
   * Will fetch the offer once in view
   * Will show the tile once both the offer AND the tile is in view
   * This is used to ensure the tile isn't mounted when out of view
   * Note: This will prevent the tile from being SSR'ed
   *
   * offer-or-inview:
   * Will fetch the offer once in view
   * Will show the tile as soon as the offer is available (from any source)
   * Used when you wish the tile to be able to SSR'ed
   *
   * none:
   * Will fetch the offer straight
   * Will show the tile as soon as the offer is ready
   *
   * @default none
   */
  lazyLoad?: 'offer-and-inview' | 'offer-or-inview' | 'none'
  wrapperClass?: string
  isMetaSearch?: boolean
  showPerNightPricing?: boolean
  showLimitedTimeLuxExclusiveTooltip?: boolean
  /**
   * Whether to prevent navigation when clicking on the tile
   * @default false
   */
  preventNavigationToOfferPage?: boolean
  storeOfferListTile?: boolean
}

const tileStyleMap = {
  search: OfferSearchTile,
  frontpage: FrontPageTile,
  hero: HeroOfferTile,
}

function calculateExtendedWindowSize(extendedAvailability: App.OfferListMetaData['extendedAvailability'] | undefined): number | undefined {
  return (extendedAvailability?.checkOut && extendedAvailability?.checkIn) ?
      calculateDaysBetweenDates(extendedAvailability.checkOut, extendedAvailability.checkIn) :
    undefined
}

/**
 * Core offer list tile that will render the appropriate tile based on the type of item given
 */
const OfferListOfferTile = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    tileStyle,
    offerId,
    position,
    filters,
    eagerLoadImage,
    className,
    lazyLoad = 'none',
    wrapperClass,
    isMetaSearch = false,
    showPerNightPricing = false,
    showLimitedTimeLuxExclusiveTooltip = false,
    preventNavigationToOfferPage = false,
    storeOfferListTile = false,
  } = props

  const currency = useContext(CurrencyContext)
  const [skipInView, setSkipInView] = useState(false)
  const [offerAvailability, setOfferAvailability] = useState<boolean | undefined>(undefined)
  const [offerPrice, setOfferPrice] = useState<number | undefined>(undefined)
  const [roomRateId, setRoomRateId] = useState<string | undefined>(undefined)
  const [offerMemberPrice, setOfferMemberPrice] = useState<number | undefined>(undefined)
  const [offerDuration, setOfferDuration] = useState<number | undefined>(undefined)
  const [offerExtendedAvailability, setOfferExtendedAvailability] = useState<App.OfferListMetaData['extendedAvailability'] | undefined>(undefined)
  // Used to lazy load the tile if turned on
  const [tileInViewRef, tileInView, entry] = useInView({
    rootMargin: '100px',
    threshold: [0, OFFER_TRACKING_IN_VIEW_THRESHOLD],
    skip: skipInView,
  })
  const tileRef = useRef<HTMLDivElement>(null)
  const setTileRef = useCallback<RefCallback<HTMLDivElement>>((instance) => {
    tileInViewRef(instance)
    tileRef.current = instance
  }, [tileInViewRef])

  useImperativeHandle(ref, () => tileRef.current!)

  useEffect(() => {
    handleSkipInView(entry, skipInView, setSkipInView)
  }, [entry, skipInView, setSkipInView, tileInView])

  const hasBeenInView = useMemo(() => calculateHasBeenInView(entry), [entry])

  const trackingInView = useMemo(() => calculateTrackingInView(entry), [entry])

  const Tile = tileStyleMap[tileStyle]
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [offer, , error] = useOffer(offerId, {
    requireSummaryOnly: true,
    disabled: lazyLoad !== 'none' && !hasBeenInView,
  })

  const [offerAvailabilityReady, setOfferAvailabilityReady] = useState(false)
  const [offerPricingReady, setOfferPricingReady] = useState(false)

  const isHotelOffer = useMemo(() => !!offer && isHotel(offer), [offer])

  useEffect(() => {
    if (isHotelOffer) return

    // We do not want to wait for pricing when the offer is not a hotel offer
    setOfferPricingReady(true)
  }, [isHotelOffer])

  const handleMouseEnter = useCallback(() => {
    if (offer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productHover,
        offer,
      })
    }
  }, [dispatchOfferListEvent, offer])

  const onProductClick = useCallback(() => {
    if (isLEOffer(offer)) {
      // TODO: remove this once G4A is up?
      // we only know how to register le offer clicks at the moment
      addGTMEvent(offerClicked(offer, currency, position))
    } else if (isCruiseOffer(offer)) {
      fireInteractionEvent(cruiseOfferTileClick(offerId))
    }

    if (offer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer,
        position,
        duration: offerDuration,
        price: offerPrice,
        memberPrice: offerMemberPrice,
        availability: offerAvailability,
        extendedAvailability: calculateExtendedWindowSize(offerExtendedAvailability),
      })
    }
  }, [offer, currency, position, offerId, dispatchOfferListEvent, offerDuration, offerPrice, offerMemberPrice, offerAvailability, offerExtendedAvailability])

  useEffect(() => {
    if (trackingInView && offer && offerAvailabilityReady && offerPricingReady) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer,
        position,
        availability: offerAvailability,
        key: EventDataKey.ProductImpression,
        duration: offerDuration,
        price: offerPrice,
        memberPrice: offerMemberPrice,
        roomRateId,
        extendedAvailability: calculateExtendedWindowSize(offerExtendedAvailability),
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView, offer, offerAvailabilityReady, offerPricingReady])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction, additionalData) => {
    if (dispatchAction.type === OfferListEvents.offerReady) {
      setOfferAvailabilityReady(true)
    } else if (dispatchAction.type === OfferListEvents.pricingCalculated) {
      setOfferPrice(dispatchAction.price)
      setRoomRateId(dispatchAction.roomRateId)
      setOfferMemberPrice(dispatchAction.memberPrice)
      setOfferDuration(dispatchAction.duration)
      setOfferExtendedAvailability(dispatchAction.extendedAvailability)
      setOfferAvailability(dispatchAction.availability)

      setOfferPricingReady(true)
    }
    dispatchOfferListEvent(dispatchAction, additionalData)
  }, [dispatchOfferListEvent])

  if (error) {
    return null
  }

  const showTile = !!(lazyLoad === 'offer-and-inview' ? offer && hasBeenInView : offer)

  return (
    <ErrorBoundary fallback={null} reportErrors={false}>
      <LoaderPlayStateContext.Provider value={hasBeenInView ? 'running' : 'paused'}>
        <div
          ref={setTileRef}
          onMouseEnter={handleMouseEnter}
          className={wrapperClass}
        >
          {!showTile && (
            <OfferListLoadingOfferTile
              className={className}
              offerId={offerId}
              tileStyle={tileStyle}
              filters={filters}
              offerPosition={position}
            />
          )}
          {showTile && (
            <OfferListEventsProvider onListEvent={onListEvent}>
              <Tile
                className={className}
                offer={offer!}
                eagerLoadFirstImage={eagerLoadImage}
                filters={filters}
                productClick={onProductClick}
                isMetaSearch={isMetaSearch}
                showPerNightPricing={showPerNightPricing}
                showLimitedTimeLuxExclusiveTooltip={showLimitedTimeLuxExclusiveTooltip}
                preventNavigationToOfferPage={preventNavigationToOfferPage}
                storeOfferSearchTile={storeOfferListTile}
                position={position}
              />
            </OfferListEventsProvider>
          )}
        </div>
      </LoaderPlayStateContext.Provider>
    </ErrorBoundary>
  )
})

OfferListOfferTile.displayName = 'OfferListOfferTile'

export default React.memo(OfferListOfferTile)
